(() => {
  function initialize(root) {
    if (root.dataset.powerReady) return;
    const get = (name) => root.querySelector(`[data-power-${name}]`);
    const system = get('system'), direction = get('direction'), source = get('source');
    const voltage = get('voltage'), pf = get('pf');
    let result = null, message = '';
    const format = (n) => n !== 0 && (Math.abs(n) < 0.001 || Math.abs(n) >= 1e9)
      ? n.toExponential(5) : Number(n.toPrecision(6)).toLocaleString('en-US', { maximumFractionDigits: 6 });
    const number = (field) => field.value.trim() === '' ? NaN : Number(field.value);

    function refresh() {
      const ac = system.value !== 'dc', three = system.value === 'three', forward = direction.value === 'kw';
      const inputUnit = forward ? 'kW' : 'A', outputUnit = forward ? 'A' : 'kW';
      get('source-label').textContent = forward ? 'Electrical input power (kW)' : 'Current (A)';
      get('source-hint').textContent = forward ? 'Use active electrical power, not kVA or motor shaft power.' : three ? 'Enter line current, not winding current.' : 'Enter the steady operating current.';
      get('voltage-label').textContent = three ? 'Line-to-line voltage (V)' : ac ? 'Supply voltage (V RMS)' : 'DC supply voltage (V)';
      get('voltage-hint').textContent = three ? 'Use voltage between phases, e.g. 400 V, not 230 V to neutral.' : ac ? 'Use the voltage across the single-phase load.' : 'Power factor is not used for DC.';
      get('pf-field').hidden = !ac;
      pf.disabled = !ac;
      get('result-label').textContent = forward ? (three ? 'Calculated line current' : 'Calculated current') : 'Calculated active power';
      get('unit').textContent = outputUnit;
      const s = number(source), v = number(voltage), factor = ac ? number(pf) : 1;
      let error = '';
      for (const [field, valid] of [[source, Number.isFinite(s) && s >= 0], [voltage, Number.isFinite(v) && v > 0], [pf, !ac || (Number.isFinite(factor) && factor > 0 && factor <= 1)]]) {
        field.setAttribute('aria-invalid', String(!valid));
      }
      if (!Number.isFinite(s) || s < 0) error = `Enter a ${forward ? 'power' : 'current'} value of zero or greater.`;
      else if (!Number.isFinite(v) || v <= 0) error = 'Enter a voltage greater than zero.';
      else if (ac && (!Number.isFinite(factor) || factor <= 0 || factor > 1)) error = 'Enter a power factor greater than 0 and up to 1.';
      const multiplier = three ? Math.sqrt(3) : 1;
      result = error ? null : forward ? (s / v) * 1000 / multiplier / factor : (s * multiplier * factor / 1000) * v;
      if (result !== null && (!Number.isFinite(result) || (s > 0 && result === 0))) {
        result = null;
        error = 'These values exceed the calculation range. Check your units and inputs.';
      }
      get('value').textContent = result === null ? '—' : format(result);
      get('error').textContent = error;
      get('error').hidden = !error;
      get('copy').disabled = result === null;
      get('copy-status').textContent = '';
      const formula = forward ? (three ? 'I = 1000 × P ÷ (√3 × V × PF)' : ac ? 'I = 1000 × P ÷ (V × PF)' : 'I = 1000 × P ÷ V') : (three ? 'P = √3 × V × I × PF ÷ 1000' : ac ? 'P = V × I × PF ÷ 1000' : 'P = V × I ÷ 1000');
      get('formula').textContent = formula;
      const numeric = forward ? `1000 × ${s} ÷ (${three ? '√3 × ' : ''}${v}${ac ? ' × ' + factor : ''})` : `${three ? '√3 × ' : ''}${v} × ${s}${ac ? ' × ' + factor : ''} ÷ 1000`;
      get('substitution').textContent = result === null ? 'Complete the inputs to see your calculation.' : `${numeric} = ${format(result)} ${outputUnit}`;
      const modeName = three ? 'Balanced three-phase AC' : ac ? 'Single-phase AC' : 'DC';
      message = [
        'kW / Amps calculation inquiry',
        `System: ${modeName}`,
        `Direction: ${forward ? 'kW to Amps' : 'Amps to kW'}`,
        `Input: ${source.value.trim() || '(not entered)'} ${inputUnit}`,
        `${three ? 'Line-to-line voltage' : 'Supply voltage'}: ${voltage.value.trim() || '(not entered)'} V`,
        ...(ac ? [`Power factor: ${pf.value.trim() || '(not entered)'}`] : []),
        `Result: ${result === null ? 'Unavailable — ' + error : format(result) + ' ' + outputUnit + ' (approx.)'}`,
        `Formula: ${formula}`,
        'Purpose: Please help me select a suitable industrial part.',
        `Page: ${root.dataset.pageUrl}`
      ].join('\n');
      get('email').setAttribute('href', 'mailto:' + root.dataset.email + '?subject=' + encodeURIComponent('kW / Amps calculation inquiry') + '&body=' + encodeURIComponent(message));
      get('whatsapp').setAttribute('href', 'https://wa.me/8613533898924?text=' + encodeURIComponent(message));
      get('contact-note').textContent = result === null ? 'You can still ask for help. Your message marks this calculation as incomplete.' : 'Your parameters and result are included. Review the message before sending.';
    }
    root.addEventListener('input', (event) => { if (event.target.matches('input')) refresh(); });
    root.addEventListener('change', (event) => {
      if (!event.target.matches('select')) return;
      // Preserve the operating point when reversing a valid calculation.
      if (event.target === direction && result !== null) source.value = String(result);
      refresh();
    });
    root.querySelectorAll('[data-power-example]').forEach((button) => {
      button.addEventListener('click', () => {
        const [mode, power, volts, factor] = button.dataset.powerExample.split(',');
        system.value = mode; direction.value = 'kw'; source.value = power; voltage.value = volts; pf.value = factor;
        refresh();
      });
    });
    get('copy').addEventListener('click', async () => {
      if (result === null) return;
      try { await navigator.clipboard.writeText(message); get('copy-status').textContent = 'Calculation copied.'; }
      catch { get('copy-status').textContent = 'Copy unavailable. Select the result or use an inquiry button.'; }
    });
    refresh();
    root.dataset.powerReady = 'true';
  }
  const init = () => document.querySelectorAll('[data-koeed-power]').forEach(initialize);
  init();
  document.addEventListener('shopify:section:load', init);
})();

